import { Fallback } from "@/src/common/components/Fallback"
import Link from "@/src/common/components/Link"
import DesktopSocialShare from "@/src/common/components/SocialShareCard/DesktopSocialShare"
import MobileSocialShare from "@/src/common/components/SocialShareCard/MobileSocialShare"
import useStearsRouter from "@/src/common/hooks/useStearsRouter"
import {
  DEFAULT_ELECTION_YEAR,
  GENDER,
  HISTORICAL_RESULTS_START_YEAR,
  RACE_TO_NAME
} from "@/src/elections/business-layer/constants"
import { URLFactory } from "@/src/elections/business-layer/factories/URLFactory"
import { personLd } from "@/src/elections/business-layer/selectors"
import {
  CandidateCampaignInfo,
  CandidateInfo,
  ElectionArticleInfo,
  Race,
  SocialShareEventProps
} from "@/src/elections/business-layer/types"
import { GetCandidatePropsUsecase } from "@/src/elections/business-layer/usecases/GetCandidatePropsUsecase"
import { useAnalytics } from "@/src/sb/components/Analytics"
import classNames from "classnames"
import { NextPage } from "next"
import { useEffect, useState } from "react"

import useElectionArticles from "../../../elections/business-layer/hooks/useElectionArticles"
import Breadcrumbs from "../../../elections/components/Breadcrumbs"
import CandidateProfile from "../../../elections/components/CandidateProfile"
import Layout from "../../../elections/components/Layout"
import Spinner from "../../../elections/components/Spinner"
import Template from "../../../elections/components/Template"
import Typography from "../../../elections/components/Typography"
import { ExternalHTTPInstance } from "../../../elections/data-layer"
import { SEO } from "../../../sb/components/SEO"

type CandidateDetailProps = {
  candidate: CandidateInfo
  campaigns: CandidateCampaignInfo[]
  articles: ElectionArticleInfo[]
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const campaignToElectionDescription = (campaign: CandidateCampaignInfo) =>
  `${campaign.year} ${
    (campaign.stateName ? cap(campaign.stateName) : "") + " "
  }${RACE_TO_NAME[campaign.race]} election`

const CandidateDetail: NextPage<CandidateDetailProps> = () => {
  const analytics = useAnalytics()
  const { query, isReady } = useStearsRouter()
  const [pageTitle, setPageTitle] = useState<string>("")
  const [loadingCandidate, setLoadingCandidate] = useState(true)
  const [candidate, setCandidate] = useState<CandidateInfo>(
    {} as CandidateCampaignInfo
  )
  const [campaigns, setCampaigns] = useState<CandidateCampaignInfo[]>([])
  const pageURL = URLFactory.absolute("candidate", candidate.slug)

  useEffect(() => setPageTitle(document.title), [])

  const getCandidateProps = async () => {
    const getCandidatePropsUsecase = new GetCandidatePropsUsecase()
    const candidateProps = await getCandidatePropsUsecase.execute(
      query.candidate as string
    )
    setCandidate(candidateProps.candidate)
    setCampaigns(candidateProps.campaigns)
    setLoadingCandidate(false)

    return candidateProps
  }

  const { articles } = useElectionArticles(
    ExternalHTTPInstance,
    undefined,
    undefined,
    []
  )

  useEffect(() => {
    if (!isReady) return

    getCandidateProps()
  }, [query.candidate])

  const campaignHistory = Object.values(
    campaigns.reduce((xCampaigns, campaign) => {
      if (xCampaigns[campaign.year as string]) {
        return xCampaigns
      }

      const raceIsPresident = campaign.race === "president"

      return {
        ...xCampaigns,
        [campaign.year]: {
          ...campaign,
          state: raceIsPresident ? undefined : campaign.state,
          stateName: raceIsPresident ? undefined : campaign.stateName
        }
      }
    }, {} as Record<string, CandidateCampaignInfo>)
  )

  useEffect(() => {
    if (candidate) {
      analytics.viewElectionCandidatePage({
        name: candidate.name,
        party: candidate.party,
        slug: candidate.slug,
        gender: candidate.gender || undefined,
        age: candidate.age ? candidate.age.toString() : undefined,
        state: candidate.state || undefined
      })
    }
  }, [analytics])

  const onClickCandidateHistory = ({
    race,
    year,
    stateName: state,
    constituency
  }: CandidateCampaignInfo) => {
    analytics.clickCandidateHistory({
      state,
      constituency,
      race,
      year
    })
  }

  const onClickArticle = ({ title, slug, href }: ElectionArticleInfo) => {
    analytics.clickArticleInFeed({
      page: href,
      title,
      slug
    })
  }

  const onClickMyElections = (
    source: "my-elections-cta" | "nav-follow-races"
  ) => {
    analytics.clickMyElections({ source })
  }

  const onSocialShare = (media: SocialShareEventProps["type"]) => {
    analytics.clickSocialShareButton({
      type: media,
      candidateSlug: candidate.slug
    })
  }

  return (
    <>
      <MobileSocialShare
        pageTitle={pageTitle}
        pageURL={pageURL}
        buttons={{ linkedin: false, email: false }}
        onClickShare={onSocialShare}
      />

      {candidate ? (
        <SEO
          title={candidate.name + " candidate data for 2023 - Stears Elections"}
          description={`Learn about ${candidate.name}. Candidate profile includes party, age, state, and gender.`}
          imageURL={URLFactory.absolute("socialCardImage")}
          linkedData={personLd({
            "@id": URLFactory.absolute("candidate", candidate.slug),
            name: candidate.name,
            age: candidate.age ? candidate.age.toString() : undefined,
            gender: candidate.gender ? GENDER[candidate.gender] : undefined
          })}
        />
      ) : null}
      <Layout onClickFollowRaces={() => onClickMyElections("nav-follow-races")}>
        <Template.Container>
          <Template.Page.TwoColumns
            showExplorePastResults={false}
            onClickMyElections={() => onClickMyElections("my-elections-cta")}
            articles={articles || []}
            onClickArticle={onClickArticle}
          >
            <Template.Card.PreElection>
              <DesktopSocialShare
                pageTitle={pageTitle}
                pageURL={pageURL}
                onClickShare={onSocialShare}
              />

              {!loadingCandidate ? (
                <>
                  <Breadcrumbs.Nav
                    className="border-b border-gray-200"
                    items={[
                      {
                        href: URLFactory.absolute(
                          "governor",
                          DEFAULT_ELECTION_YEAR
                        ),
                        text: "Home"
                      },
                      { text: "Candidates" },
                      { text: candidate?.name }
                    ]}
                  />
                  <main className="px-6 py-8">
                    <Typography type="p15" className="text-gray-800">
                      Candidate Profile
                    </Typography>
                    {candidate ? (
                      <CandidateProfile {...candidate} className="mt-3" />
                    ) : (
                      <div className="w-full flex justify-center p-20">
                        <Spinner />
                      </div>
                    )}

                    {campaignHistory.length ? (
                      <>
                        <Typography
                          type="p16"
                          weight="medium"
                          className="text-black mt-10 mb-4"
                        >
                          Election History for {candidate?.name} since{" "}
                          {HISTORICAL_RESULTS_START_YEAR}
                        </Typography>
                        <div className="mt-3 first:p-6 border border-gray-200">
                          {campaignHistory
                            ?.sort(
                              (a, b) => parseInt(b.year) - parseInt(a.year)
                            )
                            .map((campaign, idx) => (
                              <Link
                                legacyBehavior
                                key={idx}
                                href={URLFactory.election(
                                  campaign.race as Race,
                                  campaign.year,
                                  campaign.state,
                                  campaign.constituency
                                )}
                              >
                                <a
                                  onClick={() =>
                                    onClickCandidateHistory(campaign)
                                  }
                                  className={classNames(
                                    "w-full block cursor-pointer",
                                    {
                                      "border-t border-gray-200": idx !== 0
                                    }
                                  )}
                                >
                                  <Typography
                                    type="p15"
                                    className="text-blue-600 p-4 bg-white"
                                  >
                                    {campaignToElectionDescription(campaign)}
                                  </Typography>
                                </a>
                              </Link>
                            ))}
                        </div>
                      </>
                    ) : null}
                  </main>
                </>
              ) : (
                <section className="text-center">
                  <Fallback logo={false} />
                  <p className="text-stears_black">Loading candidate data...</p>
                </section>
              )}
            </Template.Card.PreElection>
          </Template.Page.TwoColumns>
        </Template.Container>
      </Layout>
    </>
  )
}

export default CandidateDetail
